import React, { useState, useEffect } from 'react';
import {
  Link, Settings, Activity, List, Shield, CheckCircle,
  AlertCircle, RefreshCw, Plus, Trash2, Eye, EyeOff, Check
} from 'lucide-react';
import { plaidApi } from '../../services/plaidApi';
import { toast } from 'react-toastify';
import './CommonStyles.css';

const badge = (status) => {
  const styles = {
    success: { background: '#ecfdf5', color: '#10b981' },
    active: { background: '#eff6ff', color: '#3b82f6' },
    posted: { background: '#ecfdf5', color: '#10b981' },
    connected: { background: '#eff6ff', color: '#3b82f6' },
    reconciled: { background: '#ecfdf5', color: '#10b981' },
    unreconciled: { background: '#fffbeb', color: '#f59e0b' },
    pending: { background: '#fffbeb', color: '#f59e0b' },
    error: { background: '#fef2f2', color: '#ef4444' },
  };
  const s = styles[status] || { background: '#f1f5f9', color: '#475569' };
  return (
    <span style={{ padding: '0.2rem 0.6rem', borderRadius: 12, fontSize: '0.75rem', fontWeight: 700, ...s }}>
      {status}
    </span>
  );
};

const PlaidConnector = () => {
  const [activeTab, setActiveTab] = useState('accounts');
  const [showSecret, setShowSecret] = useState(false);
  const [env, setEnv] = useState('sandbox');
  const [clientId, setClientId] = useState('');
  const [secret, setSecret] = useState('');
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const [items, setItems] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [reconcilingId, setReconcilingId] = useState(null);

  const [savingCredentials, setSavingCredentials] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [itemsRes, accountsRes, txRes, credsRes] = await Promise.allSettled([
        plaidApi.getItems(),
        plaidApi.getAccounts(),
        plaidApi.getTransactions(),
        plaidApi.getCredentials(),
      ]);

      if (itemsRes.status === 'fulfilled') {
        const val = itemsRes.value || {};
        setItems(val.data !== undefined ? (val.data || []) : (Array.isArray(val) ? val : []));
      }
      if (accountsRes.status === 'fulfilled') {
        const val = accountsRes.value || {};
        setAccounts(val.data !== undefined ? (val.data || []) : (Array.isArray(val) ? val : []));
      }
      if (txRes.status === 'fulfilled') {
        const val = txRes.value || {};
        setTransactions(val.data?.data !== undefined ? val.data.data : (val.data !== undefined ? val.data : (Array.isArray(val) ? val : [])));
      }
      if (credsRes.status === 'fulfilled') {
        const c = credsRes.value?.data || credsRes.value || {};
        if (c.environment) setEnv(c.environment);
        if (c.client_id) setClientId(c.client_id);
        if (c.secret) setSecret(c.secret);
      }
    } catch (err) {
      /* console.error('Error fetching Plaid data:', err); */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await plaidApi.syncTransactions();
      await fetchData();
      toast.success(res.message || 'Bank transactions synced successfully!');
    } catch (err) {
      toast.error('Sync failed: ' + err.message);
    } finally {
      setSyncing(false);
    }
  };

  const handleRemoveItem = async (id) => {
    if (!window.confirm('Are you sure you want to disconnect this bank connection?')) return;
    try {
      const res = await plaidApi.removeItem(id);
      await fetchData();
      toast.success(res.message || 'Bank connection removed successfully.');
    } catch (err) {
      toast.error('Failed to remove item: ' + err.message);
    }
  };

  const handleReconcile = async (txId, currentStatus) => {
    setReconcilingId(txId);
    try {
      const res = await plaidApi.reconcileTransaction({
        plaid_transaction_id: txId,
        is_reconciled: !currentStatus,
      });
      await fetchData();
      toast.success(res.message || 'Transaction reconciliation updated.');
    } catch (err) {
      toast.error('Reconciliation failed: ' + err.message);
    } finally {
      setReconcilingId(null);
    }
  };

  const openPlaidLink = async () => {
    try {
      const res = await plaidApi.createLinkToken();
      const linkToken = res.link_token || res.data?.link_token;
      if (!linkToken) {
        throw new Error(res.message || 'Failed to get Plaid link token');
      }

      if (!window.Plaid) {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }

      const handler = window.Plaid.create({
        token: linkToken,
        onSuccess: async (public_token, metadata) => {
          setLoading(true);
          try {
            await plaidApi.exchangePublicToken(public_token, metadata);
            toast.success('Bank account linked successfully! Syncing accounts...');
            // Wait a moment for the backend to fetch and store accounts from Plaid
            await new Promise(resolve => setTimeout(resolve, 2000));
            await fetchData();
          } catch (err) {
            toast.error('Token exchange failed: ' + err.message);
          } finally {
            setLoading(false);
          }
        },
        onExit: (err) => {
          if (err) {
            /* console.error('Plaid Link exited with error:', err); */
          }
        },
      });

      handler.open();
    } catch (err) {
      toast.error('Plaid Link Error: ' + err.message);
    }
  };

  const tabs = [
    { id: 'accounts', label: 'Linked Accounts', icon: <Link size={14} /> },
    { id: 'transactions', label: 'Bank Transactions', icon: <Activity size={14} /> },
    { id: 'setup', label: 'API Credentials', icon: <Settings size={14} /> },
  ];

  const handleSaveCredentials = async (e) => {
    e.preventDefault();
    if (!clientId.trim() || !secret.trim()) {
      toast.warning('Please enter both Client ID and Secret');
      return;
    }
    setSavingCredentials(true);
    try {
      const res = await plaidApi.saveCredentials({
        client_id: clientId,
        secret: secret,
        environment: env,
      });
      toast.success(res.message || 'API Credentials saved successfully!');
    } catch (err) {
      toast.error('Failed to save credentials: ' + err.message);
    } finally {
      setSavingCredentials(false);
    }
  };

  /* ── Setup Tab ─────────────────────────────────────────────── */
  const renderSetup = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <form onSubmit={handleSaveCredentials} style={{ background: 'var(--etpos-card)', borderRadius: 10, border: '1px solid var(--etpos-border)', padding: '2rem' }}>
        <h3 style={{ margin: '0 0 1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '1.1rem' }}>
          <Settings size={18} color="var(--etpos-primary)" /> API Credentials (.env Configuration)
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem', marginBottom: '1.5rem' }}>
          <div>
            <label style={{ display: 'block', fontWeight: 700, marginBottom: '0.4rem', fontSize: '0.875rem' }}>Environment</label>
            <select value={env} onChange={e => setEnv(e.target.value)}
              style={{ width: '100%', padding: '0.7rem', border: '1px solid var(--etpos-border)', borderRadius: 8, fontSize: '0.9rem', background: 'var(--etpos-card)', color: 'var(--etpos-text)' }}>
              <option value="sandbox">Sandbox (Testing)</option>
              <option value="development">Development</option>
              <option value="production">Production</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 700, marginBottom: '0.4rem', fontSize: '0.875rem' }}>PLAID_CLIENT_ID</label>
            <input type="text" placeholder="Set in .env file" value={clientId} onChange={e => setClientId(e.target.value)}
              style={{ width: '100%', padding: '0.7rem', border: '1px solid var(--etpos-border)', borderRadius: 8, fontSize: '0.9rem', boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 700, marginBottom: '0.4rem', fontSize: '0.875rem' }}>PLAID_SECRET</label>
            <div style={{ position: 'relative' }}>
              <input type={showSecret ? 'text' : 'password'} placeholder="Set in .env file" value={secret} onChange={e => setSecret(e.target.value)}
                style={{ width: '100%', padding: '0.7rem 2.5rem 0.7rem 0.7rem', border: '1px solid var(--etpos-border)', borderRadius: 8, fontSize: '0.9rem', boxSizing: 'border-box' }} />
              <button type="button" onClick={() => setShowSecret(!showSecret)}
                style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--etpos-text-muted)' }}>
                {showSecret ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="submit" disabled={savingCredentials} className="add-btn" style={{ padding: '0.65rem 1.75rem', fontWeight: '700' }}>
            {savingCredentials ? '⏳ Saving...' : '💾 Save API Credentials'}
          </button>
        </div>
      </form>
    </div>
  );

  /* ── Linked Accounts Tab ───────────────────────────────────── */
  const renderAccounts = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem' }}>
        {items.length === 0 && accounts.length === 0 ? (
          <div style={{ gridColumn: '1 / -1', background: 'var(--etpos-card)', borderRadius: 12, border: '1px solid var(--etpos-border)', padding: '3rem 2rem', textAlign: 'center' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>🏦</div>
            <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.2rem' }}>No Bank Accounts Linked Yet</h3>
            <p style={{ margin: '0 0 1.5rem', color: 'var(--etpos-text-muted)', fontSize: '0.9rem' }}>
              Connect your bank institution via Plaid Link to start syncing accounts and transaction feeds.
            </p>
            <button className="add-btn" style={{ padding: '0.75rem 2rem' }} onClick={openPlaidLink}>
              <Plus size={16} /> Connect Bank Account
            </button>
          </div>
        ) : (
          accounts.map(acc => (
            <div key={acc.id} style={{ background: 'var(--etpos-card)', borderRadius: 12, border: '1px solid var(--etpos-border)', padding: '1.5rem', position: 'relative' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div style={{ width: 42, height: 42, background: '#eff6ff', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.25rem' }}>🏦</div>
                  <div>
                    <p style={{ margin: 0, fontWeight: 700, fontSize: '0.95rem' }}>{acc.official_name || acc.name}</p>
                    <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--etpos-text-muted)' }}>•••• {acc.mask || 'N/A'}</p>
                  </div>
                </div>
                {badge(acc.is_active ? 'active' : 'inactive')}
              </div>
              <p style={{ margin: '0 0 0.25rem', fontSize: '0.82rem', color: 'var(--etpos-text-muted)' }}>{acc.type} {acc.subtype ? `· ${acc.subtype}` : ''}</p>
              <p style={{ margin: '0 0 1rem', fontSize: '1.4rem', fontWeight: 800, color: '#3b82f6' }}>
                ${Number(acc.current_balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </p>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button className="export-btn" style={{ flex: 1, justifyContent: 'center' }} onClick={handleSync} disabled={syncing}>
                  <RefreshCw size={13} className={syncing ? 'spin-animation' : ''} /> Sync
                </button>
                <button onClick={() => handleRemoveItem(acc.plaid_item_id)}
                  style={{ padding: '0.4rem 0.75rem', border: '1px solid #fecaca', borderRadius: 6, background: '#fff', color: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.82rem' }}>
                  <Trash2 size={13} /> Remove
                </button>
              </div>
            </div>
          ))
        )}

        {accounts.length > 0 && (
          <div onClick={openPlaidLink}
            style={{ background: 'var(--etpos-bg)', borderRadius: 12, border: '2px dashed var(--etpos-border)', padding: '1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', cursor: 'pointer', minHeight: 180, transition: 'all 0.2s' }}
            onMouseEnter={e => e.currentTarget.style.borderColor = '#3b82f6'}
            onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--etpos-border)'}>
            <div style={{ width: 48, height: 48, background: '#eff6ff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Plus size={22} color="#3b82f6" />
            </div>
            <p style={{ margin: 0, fontWeight: 700, color: '#3b82f6', fontSize: '0.95rem' }}>Link New Institution</p>
            <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--etpos-text-muted)', textAlign: 'center' }}>Opens Plaid Link widget</p>
          </div>
        )}
      </div>

      {/* Stats Row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem' }}>
        {[
          { label: 'Linked Institutions', value: items.length, icon: '🏦', color: '#3b82f6' },
          { label: 'Total Bank Balance', value: `$${accounts.reduce((s, a) => s + Number(a.current_balance || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`, icon: '💰', color: '#10b981' },
          { label: 'Synced Transactions', value: transactions.length, icon: '📄', color: '#f59e0b' },
        ].map((s, i) => (
          <div key={i} style={{ background: 'var(--etpos-card)', borderRadius: 10, border: '1px solid var(--etpos-border)', padding: '1.25rem', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>{s.icon}</div>
            <p style={{ margin: 0, fontWeight: 800, fontSize: '1.3rem', color: s.color }}>{s.value}</p>
            <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--etpos-text-muted)' }}>{s.label}</p>
          </div>
        ))}
      </div>
    </div>
  );

  /* ── Transactions Tab ──────────────────────────────────────── */
  const renderTransactions = () => (
    <div style={{ background: 'var(--etpos-card)', borderRadius: 10, border: '1px solid var(--etpos-border)', overflow: 'hidden' }}>
      <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--etpos-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700 }}>Bank Transactions Feed & Reconciliation</h3>
        <button className="export-btn" onClick={handleSync} disabled={syncing}>
          <RefreshCw size={13} className={syncing ? 'spin-animation' : ''} /> Sync Transactions
        </button>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="products-table" style={{ minWidth: 800 }}>
          <thead>
            <tr>
              <th>DATE</th><th>DESCRIPTION</th><th>AMOUNT</th><th>ACCOUNT</th><th>RECONCILED</th><th>ACTION</th>
            </tr>
          </thead>
          <tbody>
            {transactions.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center', padding: '2rem', color: 'var(--etpos-text-muted)' }}>
                  No transactions synced yet. Click "Sync Transactions" to pull data.
                </td>
              </tr>
            ) : (
              transactions.map(t => (
                <tr key={t.id}>
                  <td>{t.date}</td>
                  <td style={{ maxWidth: 260 }}>{t.name}</td>
                  <td style={{ fontWeight: 700, color: t.amount < 0 ? '#10b981' : '#ef4444' }}>
                    {t.amount < 0 ? '+' : '-'}${Math.abs(t.amount).toFixed(2)}
                  </td>
                  <td>{t.account?.name || 'Bank Account'}</td>
                  <td>{badge(t.is_reconciled ? 'reconciled' : 'unreconciled')}</td>
                  <td>
                    <button
                      onClick={() => handleReconcile(t.id, t.is_reconciled)}
                      disabled={reconcilingId === t.id}
                      className="export-btn"
                      style={{ padding: '0.3rem 0.6rem', fontSize: '0.78rem' }}
                    >
                      {t.is_reconciled ? 'Unmatch' : 'Match / Reconcile'}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="module-container">
      {/* Header */}
      <div className="module-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem' }}>
          <div style={{ background: 'var(--etpos-card, #e2e8f0)', padding: '1rem', borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 68, height: 68, boxSizing: 'border-box' }}>
            <svg width="44" height="28" viewBox="0 0 60 40" fill="none">
              <rect width="60" height="40" rx="8" fill="#001c40" />
              <text x="30" y="27" textAnchor="middle" fontFamily="Arial, sans-serif" fontWeight="900" fontSize="19" fill="white" letterSpacing="-0.5">plaid</text>
            </svg>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
            <h2 style={{ margin: 0, fontSize: '1.85rem', fontWeight: 800, color: 'var(--etpos-text, #001c40)', letterSpacing: '-0.02em' }}>Plaid Bank Integration</h2>
            <p style={{ margin: 0, fontSize: '0.95rem', color: 'var(--etpos-text-muted, #475569)', fontWeight: 500 }}>
              Connect Bank Accounts · Live Feeds · Auto Reconciliation
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button className="add-btn" onClick={openPlaidLink}>
            <Plus size={14} /> Connect Bank Account
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="module-tabs">
        {tabs.map(t => (
          <button key={t.id} className={`tab-btn ${activeTab === t.id ? 'active' : ''}`}
            onClick={() => setActiveTab(t.id)}
            style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="module-content" style={{ padding: '0.5rem', background: 'transparent', border: 'none', boxShadow: 'none' }}>
        {activeTab === 'accounts' && renderAccounts()}
        {activeTab === 'transactions' && renderTransactions()}
        {activeTab === 'setup' && renderSetup()}
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
        .spin-animation { animation: plaid-spin 1.2s linear infinite; }
        @keyframes plaid-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}} />
    </div>
  );
};

export default PlaidConnector;
